import { useState, useEffect, useRef } from 'react';
import Image from 'components/shared/image';
import SiteLink from 'components/shared/sitelink';
import RichText from 'components/shared/richtext';
import ArrowRight from 'components/shared/arrow-right';
import ArrowLeft from 'components/shared/arrow-left';
import useIsMobile from 'hooks/utils/useIsMobile';
import { GlobalFilterProvider } from 'hooks/filterGlobalState/useFilterGlobalState';
import BreadCrumbs from 'components/layout/breadcrumbs/breadcrumbs';
import { addLocaleToHref } from 'lib/utils/helpers';
import { useRouter } from 'next/router';
import Quiz from 'components/sections/quiz';
import styles from './collection.module.scss';
import ProductsHeader from './productsHeader/productsHeader';
import CollectionProducts from './collectionProducts';
import SeoBlock from './seoBlock/seoBlock';
import { useCart } from '../../../hooks';
import BackToTopButton from './backToTopButton';

const PageCollection = ({ data, locale, resources }) => {
  const {
    title,
    link,
    filterTypes,
    categories,
    breadcrumbs,
    headlineBody,
    products,
    seoBlock,
    ctasToAppear,
    slug,
    showAttributeLabel,
    attributeLabelBackgroundColor,
    attributeLabelTextColor,
    showBackToTopButton,
    quiz,
  } = data;

  const resourcesCollection = resources.collection;
  const isMobile = useIsMobile();
  const collectionCategoriesRef = useRef();
  const [categoryWidth, setCategoryWidth] = useState();
  const [carouselPositioning, setCarouselPositioning] = useState({
    atLeftSide: true,
    atRightSide: false,
  });
  // This is to initialize the cart when the customer is navigating to a collection page
  const { cart } = useCart();
  const router = useRouter();

  const renderCorrectImage = image => {
    if (image) return isMobile ? image.mobile : image.desktop;
    return null;
  };

  const updateCarouselPositioning = () => {
    setCarouselPositioning({
      atLeftSide: collectionCategoriesRef.current?.scrollLeft === 0,
      atRightSide:
        collectionCategoriesRef.current?.scrollLeft +
          collectionCategoriesRef.current?.clientWidth >=
        collectionCategoriesRef.current?.scrollWidth,
    });
  };

  // This is the width of a category.
  useEffect(() => {
    setCategoryWidth(collectionCategoriesRef.current?.children[0]?.clientWidth);
  }, [collectionCategoriesRef, collectionCategoriesRef.current]);

  // set scroll restoration to manual
  useEffect(() => {
    if ('scrollRestoration' in history && history.scrollRestoration !== 'manual') {
      history.scrollRestoration = 'manual';
    }
  }, []);

  // handle and store scroll position
  useEffect(() => {
    const saveScrollPosition = () => {
      const scrollPosition = document.documentElement.scrollTop;
      sessionStorage.setItem(`scrollPosition-${slug}`, scrollPosition);
    };

    router.events.on('routeChangeStart', saveScrollPosition);
    return () => {
      router.events.off('routeChangeStart', saveScrollPosition);
    };
  }, [router.events, slug]);

  // Restore scroll position
  useEffect(() => {
    if (products && products.length > 0) {
      setTimeout(() => {
        const scrollPosition = sessionStorage.getItem(`scrollPosition-${slug}`);
        if (scrollPosition) {
          window.scrollTo({
            top: Number(scrollPosition),
            behavior: 'smooth',
          });
          sessionStorage.removeItem(`scrollPosition-${slug}`);
        }
      }, 100);
    }
  }, [products, slug]);

  let timeout;
  return (
    <section>
      {breadcrumbs && <BreadCrumbs locale={locale} data={breadcrumbs} />}
      <h1 className={styles.collectionTitle}>{title}</h1>
      <RichText className={styles.headlineBody} text={headlineBody?.json} />
      {link ? (
        <SiteLink
          locale={locale}
          href={addLocaleToHref(link.slug, locale, router)}
          className={styles.collectionLink}
        >
          {link.title}
          <span className={styles.collectionArrow}>
            <ArrowRight width="1.8rem" height="1.8rem" />
          </span>
        </SiteLink>
      ) : (
        <></>
      )}
      {quiz && (
        <Quiz
          data={{ ...quiz, resources }}
          locale={locale}
          className={styles.quizSection}
          fromCollection
        />
      )}
      <div className={styles.collectionWrapper}>
        {!isMobile && categories?.length > 10 && (
          <>
            {!carouselPositioning.atRightSide && (
              <button
                type="button"
                className={styles.nextButton}
                onClick={() => {
                  collectionCategoriesRef.current.scrollBy(categoryWidth, 0);
                }}
              >
                <ArrowRight width={!isMobile ? 8 : 8} height={!isMobile ? 8 : 8} />
              </button>
            )}
            {!carouselPositioning.atLeftSide && (
              <button
                type="button"
                className={styles.previousButton}
                onClick={() => {
                  collectionCategoriesRef.current.scrollBy(-categoryWidth, 0);
                }}
              >
                <ArrowLeft width={!isMobile ? 8 : 8} height={!isMobile ? 8 : 8} />
              </button>
            )}
          </>
        )}
        {categories?.length > 0 && (
          <div
            className={
              categories.length <= 10 ? styles.justifiedCategories : styles.collectionCategories
            }
            ref={collectionCategoriesRef}
            onScroll={() => {
              clearTimeout(timeout);
              timeout = setTimeout(() => updateCarouselPositioning(), 200);
            }}
          >
            {categories.map(category => (
              <SiteLink
                key={category.slug}
                locale={locale}
                href={addLocaleToHref(category.slug, locale, router)}
                className={styles.category}
              >
                <div className={styles.categoryOuterSphere}>
                  <div className={styles.categoryInnerSphere}>
                    <Image
                      src={renderCorrectImage(category.image)}
                      className={styles.categoryImage}
                      layout="fill"
                      sizes="(max-width: 899px) 50vw, 25vw"
                      loading="eager"
                      priority
                    />
                  </div>
                </div>
                <p className={styles.categoryName}>{category.title}</p>
              </SiteLink>
            ))}
          </div>
        )}
      </div>
      <GlobalFilterProvider products={products}>
        <ProductsHeader products={products} filters={filterTypes} resources={resourcesCollection} />
        <CollectionProducts
          locale={locale}
          products={products}
          attributeLabelBackgroundColor={attributeLabelBackgroundColor}
          attributeLabelTextColor={attributeLabelTextColor}
          showAttributeLabel={showAttributeLabel}
          ctasToAppear={ctasToAppear}
          globalModules={resources}
          pageTitle={title}
        />
      </GlobalFilterProvider>
      {seoBlock && seoBlock.length > 0 && <SeoBlock data={seoBlock[0]} locale={locale} />}
      {showBackToTopButton && (
        <BackToTopButton className={styles.backToTopButton} globalModules={resources} />
      )}
    </section>
  );
};

export default PageCollection;
